import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * Admin — Paid Orders + Coach Assignment.
 * Uses HTTP Basic auth. Lets admin assign any paid order to any
 * approved coach. Assigning after the customer has submitted also
 * flips workspace_status → in_review. Un-assigning reverts.
 */
export default function AdminOrders() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({});

  const authHeader = () => ({
    headers: { Authorization: "Basic " + btoa(`${username}:${password}`) },
  });

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const [oRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, authHeader()),
        axios.get(`${API}/admin/coaches`, authHeader()),
      ]);
      setOrders(oRes.data);
      setCoaches(cRes.data);
      setAuthed(true);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid credentials." : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const [oRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, authHeader()),
        axios.get(`${API}/admin/coaches`, authHeader()),
      ]);
      setOrders(oRes.data);
      setCoaches(cRes.data);
    } catch {
      toast.error("Failed to refresh.");
    }
  };

  const assign = async (orderId, coachId) => {
    setBusy((b) => ({ ...b, [orderId]: true }));
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/assign`,
        { coach_id: coachId || null },
        authHeader(),
      );
      toast.success(coachId ? "Coach assigned." : "Coach unassigned.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assign failed.");
    } finally {
      setBusy((b) => {
        const { [orderId]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const filtered = orders.filter((o) => {
    if (filter === "unassigned") return !o.coach_id;
    if (filter === "assigned") return !!o.coach_id && o.workspace_status !== "completed";
    if (filter === "completed") return o.workspace_status === "completed";
    return true;
  });

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="admin-orders-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-center gap-6 text-[11px] tracking-[0.3em] uppercase text-white/60">
          <Link to="/" className="hover:text-[#2A5BFF]">← Back</Link>
          <Link to="/admin" className="hover:text-[#2A5BFF]">Waitlist</Link>
          <Link to="/admin/services" className="hover:text-[#2A5BFF]">Services</Link>
          <Link to="/admin/audit-log" className="hover:text-[#2A5BFF]">Audit</Link>
          <Link to="/admin/users" className="hover:text-[#2A5BFF]" data-testid="admin-orders-users-link">Users</Link>
        </div>
        <h1 className="font-anton uppercase text-5xl md:text-6xl mt-8">Admin — Orders</h1>

        {!authed && (
          <form onSubmit={load} className="mt-10 max-w-md border border-white/10 p-6" data-testid="admin-orders-login">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Username</label>
            <input
              className="input-line focus-ring"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              data-testid="admin-orders-username"
              required
            />
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
            <input
              type="password"
              className="input-line focus-ring"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-orders-password"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
              data-testid="admin-orders-login-button"
            >
              {loading ? "…" : "Sign In"}
            </button>
          </form>
        )}

        {authed && (
          <div className="mt-10" data-testid="admin-orders-content">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-[11px] tracking-[0.28em] uppercase text-white/50">
                {filtered.length} / {orders.length} paid orders · {coaches.length} approved coach{coaches.length !== 1 ? "es" : ""}
              </div>
              <div className="flex items-center gap-1">
                {["all", "unassigned", "assigned", "completed"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border ${filter === f ? "border-[#2A5BFF] text-[#2A5BFF]" : "border-white/15 text-white/60 hover:text-white"}`}
                    data-testid={`admin-orders-filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {coaches.length === 0 && (
              <div className="border border-yellow-500/40 bg-yellow-500/[0.06] p-4 mb-6 text-sm text-yellow-200" data-testid="admin-orders-no-coaches">
                No approved coaches yet.{" "}
                <Link to="/admin/users" className="underline">Promote &amp; approve a coach first</Link>{" "}
                before you can assign orders.
              </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.28em] uppercase text-white/40 border-b border-white/10">
                  <tr>
                    <th className="p-4 text-left">Order</th>
                    <th className="p-4 text-left">Customer</th>
                    <th className="p-4 text-left">Service</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-left">Amount</th>
                    <th className="p-4 text-left w-64">Assigned Coach</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const disabled = coaches.length === 0 || o.workspace_status === "completed";
                    return (
                      <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-order-row-${o.id}`}>
                        <td className="p-4 text-white/70">
                          <div className="font-mono text-[11px]">{o.id.slice(0, 8)}</div>
                          <div className="text-[10px] text-white/40">{new Date(o.created_at).toLocaleDateString()}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-white/90">{o.customer_first_name || "—"}</div>
                          <div className="text-[11px] text-white/50">{o.customer_email || ""}</div>
                        </td>
                        <td className="p-4 text-white/80">{o.service_title}</td>
                        <td className="p-4">
                          <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-white/20 text-white/80">
                            {(o.workspace_status || "—").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="p-4 text-white/70">{(o.currency || "").toUpperCase()} {Number(o.amount).toFixed(2)}</td>
                        <td className="p-4">
                          <select
                            value={o.coach_id || ""}
                            onChange={(e) => assign(o.id, e.target.value)}
                            disabled={disabled || !!busy[o.id]}
                            className="w-full bg-[#0A0A14] border border-white/15 px-3 py-2 text-white/90 text-sm focus:outline-none focus:border-[#2A5BFF] disabled:opacity-40"
                            data-testid={`assign-select-${o.id}`}
                          >
                            <option value="">— Unassigned —</option>
                            {coaches.map((c) => (
                              <option key={c.id} value={c.id}>{c.first_name} · {c.email}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-white/40 text-sm">No orders match this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
